// @ts-ignore
// @ts-nocheck
import {
  applySnapshot,
  cast,
  flow,
  getEnv,
  getParent,
  getSnapshot,
  types,
} from "mobx-state-tree";
import TutorPricingStore from "./tutorPricing";

const CertificationStore = types.model("CertificationStore", {
  name: types.optional(types.string, ""),
  institution: types.optional(types.string, ""),
});

const TutorSubject = types
  .model("SubjectStore", {
    id: types.optional(types.number, 0),
    name: types.optional(types.string, ""),
    title: types.maybeNull(types.string),
    description: types.optional(types.string, ""),
    certifications: types.optional(types.array(CertificationStore), [
      { name: "", institution: "" },
    ]),
    certification: types.optional(CertificationStore, {}),
    hourRate: types.optional(types.number, 0),
    hasUpdatedPrice: types.optional(types.boolean, false),
    status: types.optional(
      types.enumeration("Status", [
        "all",
        "active",
        "pending",
        "modification",
        "denied",
        "suspended",
      ]),
      "suspended",
    ),
  })
  .views((self) => {
    return {
      get initialFormData() {
        return getSnapshot(self);
      },
    };
  })
  .actions((self) => {
    return {
      setCertification(value, type) {
        if (type === "name") {
          self.certification.name = value;
        } else {
          self.certification.institution = value;
        }
      },
    };
  });

const PaginationStore = types
  .model("PaginationStore", {
    contents: types.optional(types.array(TutorSubject), []),
    currentPage: types.optional(types.integer, 1),
    contentsPerPage: types.optional(types.integer, 10),
  })
  .views((self) => {
    return {
      get totalContentsCount() {
        return self.contents.length;
      },

      get totalPages() {
        return Math.ceil(this.totalContentsCount / self.contentsPerPage);
      },

      get hasNextPage() {
        if (self.currentPage < this.totalPages) {
          return true;
        }
      },

      get hasPreviousPage() {
        if (self.currentPage > 1) {
          return true;
        }
      },
    };
  })
  .actions((self) => {
    return {
      setCurrentPage(page) {
        self.currentPage = page;
      },

      setPreviousPage() {
        const previous = self.currentPage - 1;
        this.setCurrentPage(previous);
      },

      setNextPage() {
        const next = self.currentPage + 1;
        this.setCurrentPage(next);
      },

      paginateContent({ contents, currentPage, contentsPerPage }) {
        const indexOfLastContent = currentPage * contentsPerPage;
        const indexOfFirstContent = indexOfLastContent - contentsPerPage;
        const currentContent = contents.slice(
          indexOfFirstContent,
          indexOfLastContent,
        );
        return currentContent;
      },
    };
  });

const TutorSubjectStore = types
  .model("SubjectStore", {
    subjects: types.optional(types.array(TutorSubject), []),
    currentSubject: types.optional(TutorSubject, {}),
    filterStatus: types.optional(types.string, ""),
    paginate: types.maybe(PaginationStore),
    pricing: types.optional(TutorPricingStore, {}),
    loading: types.optional(types.boolean, false),
  })
  .views((self) => {
    return {
      get totalSubjects() {
        return self.subjects.length;
      },
      get totalPendingSubjects() {
        return self.subjects.filter((item) => item.status === "pending").length;
      },
      get totalDeniedSubjects() {
        return self.subjects.filter((item) => item.status === "denied").length;
      },
      get totalActiveSubjects() {
        return self.subjects.filter((item) => item.status === "active").length;
      },
      get totalRequiresModification() {
        return self.subjects.filter((item) => item.status === "modification")
          .length;
      },
      get location() {
        const parent = getParent(self, 1);
        return parent.locationInfo;
      },
      get filteredSubjects() {
        const status = self.filterStatus;
        // const statusMap = {
        //   "Active Subjects": "Active",
        //   "Pending Approval": "Pending",
        //   "Denied Subjects": "Denied",
        //   "Requires Modification": "Requires Modification",
        //   "Total Subjects": "Total",
        // };
        if (status) {
          if (status !== "all") {
            return self.subjects.filter((item) => item.status === status);
          } else {
            return self.subjects;
          }
        }
        return self.subjects;
      },
    };
  })
  .actions((self) => {
    const { adapter } = getEnv(self);
    const initializePagination = () => {
      self.paginate = PaginationStore.create({
        contents: getSnapshot(self.filteredSubjects),
      });
    };

    const updateSubject = (values) => {
      applySnapshot(self.currentSubject, values);
      const others = self.subjects.filter(
        (subject) => self.currentSubject.name !== subject.name,
      );
      others.unshift(getSnapshot(self.currentSubject));
      self.subjects = cast(others);
    };

    const transformSubject = (data, type = "fetch") => {
      if (type === "fetch") {
        const { urls, object_list } = data;

        const statusObject = urls.map((url) => {
          return {
            statusCode: url.status,
            status: url.text,
          };
        });

        const subjects = object_list.map((subject) => {
          return {
            id: subject.pk,
            name: subject.skill.name,
            title: subject.heading,
            description: subject.description,
            hourRate: parseFloat(subject.price),
            status: statusObject.find(
              (item) => item.statusCode === subject.status,
            )?.status,
            certifications:
              subject.certifications.length === 0
                ? [{ name: "", institution: "" }]
                : subject.certifications.map((cert) => {
                    return {
                      name: cert.award_name ? cert.award_name : "",
                      institution: cert.award_institution
                        ? cert.award_institution
                        : "",
                    };
                  }),
            hasUpdatedPrice: subject.has_updated_price,
          };
        });
        return subjects;
      }

      if (type === "save") {
        let certifications = [];
        const isEmptyCert =
          data.certifications.find(
            (cert) => cert.name === "" && cert.institution === "",
          )?.name === "";
        if (!isEmptyCert) {
          certifications = data.certifications.map((cert) => {
            return {
              award_name: cert.name,
              award_institution: cert.institution,
            };
          });
        }
        const subject = {
          pk: data.id,
          skill: {
            name: data.name,
          },
          heading: data.title,
          description: data.description,
          price: data.hourRate,
          certifications,
        };
        // console.log(subject);
        return subject;
      }
    };

    // const fetchTutorSubjects = flow(function* fetchTutorSubjects() {
    //   try {
    //     const response = yield adapter.fetchTutorSubjects();
    //     const subjects = transformSubject(response);
    //     self.subjects = cast(subjects);
    //     applySnapshot(self.pricing, {
    //       subjects: self.subjects.map(({ name }) => ({ name })),
    //     });
    //     initializePagination();
    //     return response;
    //   } catch (error) {
    //     throw error;
    //   }
    // });

    const updateTutorSubject = flow(function* updateTutorSubject(values) {
      try {
        self.loading = true;
        const data = transformSubject(values, "save");
        const response = yield adapter.updateTutorSubject(data);
        self.loading = false;
        updateSubject(values);
        return response;
      } catch (error) {
        self.loading = false;
        throw error;
      }
    });

    const deleteSubject = flow(function* deleteSubject(subject) {
      try {
        const response = yield adapter.deleteSubject(subject);
        if (response) {
          self.subjects = cast(
            self.subjects.filter((item) => subject.name !== item.name),
          );
          initializePagination();
          return response;
        }
      } catch (error) {
        throw error;
      }
    });
    return {
      deleteSubject,
      initializePagination,
      // fetchTutorSubjects,
      updateTutorSubject,
      initializeSubjectStore(data) {
        const subjects = transformSubject(data);
        self.subjects = cast(subjects);
        applySnapshot(self.pricing, {
          subjects: self.subjects.map(({ name }) => ({ name })),
        });
        initializePagination();
      },
      setCurrentSubject(values) {
        const {
          title,
          status,
          description,
          certifications,
          hourRate,
          hasUpdatedPrice,
          name,
          id,
        } = values;
        applySnapshot(self.currentSubject, {
          id,
          name,
          title,
          status,
          hourRate,
          hasUpdatedPrice,
          description,
        });
        self.currentSubject.certifications = cast(getSnapshot(certifications));
        self.pricing.currentSubject = self.currentSubject.name;
      },
      setFilterStatus(status) {
        self.filterStatus = status;
      },

      setPrice(value) {
        if (!self.currentSubject.hasUpdatedPrice) {
          self.currentSubject.hasUpdatedPrice = true;
        }
        self.currentSubject.hourRate = parseInt(value);
      },
      afterCreate() {
        initializePagination();
      },
    };
  });

export default TutorSubjectStore;
